import { useState, type FormEvent } from 'react'
import { submitMcpWaitlist, WaitlistNotConfiguredError } from '../utils/mcpWaitlistApi'
import { trackEvent } from '../utils/analytics'

export type WaitlistPreviewState = 'live' | 'success' | 'already_registered' | 'error'
export type WaitlistField = 'email' | 'workflow' | 'goal'
export type WaitlistStatus = 'idle' | 'submitting' | 'success' | 'already_registered' | 'error'

export const GOAL_MAX = 500
const FIELD_ORDER: WaitlistField[] = ['email', 'workflow', 'goal']

export const waitlistFieldId = (field: WaitlistField) => `mcp-waitlist-${field}`

function validate(values: Record<WaitlistField, string>) {
  const errors: Partial<Record<WaitlistField, string>> = {}
  if (!values.email.trim()) errors.email = 'Enter your work email.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = 'Enter a valid email address.'
  if (!values.workflow) errors.workflow = 'Choose the workflow you want to connect first.'
  if (values.goal.length > GOAL_MAX) errors.goal = `Keep it under ${GOAL_MAX} characters.`
  return errors
}

/**
 * MCP waitlist form state. `preview` simulates outcomes for design review only
 * (nothing is sent); the live path shows success only after the endpoint saves.
 */
export function useMcpWaitlist(preview: WaitlistPreviewState = 'live') {
  const [values, setValues] = useState<Record<WaitlistField, string>>({ email: '', workflow: '', goal: '' })
  const [errors, setErrors] = useState<Partial<Record<WaitlistField, string>>>({})
  const [status, setStatus] = useState<WaitlistStatus>('idle')
  const [errorKind, setErrorKind] = useState<'unavailable' | 'failed' | null>(null)

  const setField = (field: WaitlistField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    if (errors[field]) setErrors(({ [field]: _removed, ...rest }) => rest)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (status === 'submitting') return
    const nextErrors = validate(values)
    setErrors(nextErrors)
    const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field])
    if (firstInvalid) {
      document.getElementById(waitlistFieldId(firstInvalid))?.focus()
      return
    }

    setStatus('submitting')
    setErrorKind(null)

    if (preview !== 'live') {
      await new Promise((resolve) => window.setTimeout(resolve, 700))
      if (preview === 'error') {
        setStatus('error')
        setErrorKind('failed')
      } else {
        setStatus(preview)
      }
      return
    }

    try {
      const result = await submitMcpWaitlist({
        email: values.email.trim(),
        workflow: values.workflow,
        goal: values.goal.trim(),
        sourcePage: window.location.pathname,
      })
      setStatus(result === 'already_registered' ? 'already_registered' : 'success')
      // Only the workflow choice is sent to analytics — never the email or free text.
      trackEvent('mcp_waitlist_submit', { workflow: values.workflow, result })
    } catch (error) {
      setStatus('error')
      setErrorKind(error instanceof WaitlistNotConfiguredError ? 'unavailable' : 'failed')
    }
  }

  const reset = () => {
    setValues({ email: '', workflow: '', goal: '' })
    setErrors({})
    setStatus('idle')
    setErrorKind(null)
  }

  return { values, errors, status, errorKind, setField, submit, reset, isPreview: preview !== 'live' }
}
